import * as SecureStore from 'expo-secure-store';
import * as Crypto from 'expo-crypto';
import { gcm } from '@noble/ciphers/aes.js';
import { pbkdf2Async } from '@noble/hashes/pbkdf2.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/ciphers/utils.js';
import { isWeb, isElectron } from './platform';

// Re-export hex utils so callers don't need to import from noble directly
export { bytesToHex, hexToBytes };

// Prevent iCloud Keychain backup of the vault encryption key — same policy as biometricStore
const KEYCHAIN_ACCESSIBLE = SecureStore.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY;

const vekKey = (projectId: number | string) => `vlt_${projectId}`;

// Plain-web target: RNW in a browser, NOT Electron, NOT native. SecureStore's
// web stub falls back to localStorage on this surface, which would persist
// any vault secret in plaintext on disk. We refuse to write secrets at all
// here — covers both the derived Vault Encryption Key (cacheVaultKey) and
// per-share one-time keys embedded in share URLs (cacheShareUrl). The user
// pays a ~1–2s PBKDF2 derive on every vault open and loses re-copyability
// of share URLs after the share modal closes; in exchange no master key or
// share OTK lives on disk. Electron is unaffected (uses safeStorage / OS
// keychain); native is unaffected (uses the device keychain with biometric
// protection).
const VAULT_PERSISTENCE_DISABLED = isWeb && !isElectron;

/** All fields that can be stored inside the AES-256-GCM encrypted blob. */
export interface DecryptedCredential {
  // Shared across types
  notes?: string;
  // Login — also used as the website URL field in share payloads
  url?: string;
  username?: string;
  password?: string;
  // Payment card
  cardholderName?: string;
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
  billingZip?: string;
  // PIN / access code
  pin?: string;
  context?: string;
  // Insurance
  memberId?: string;
  groupNumber?: string;
  planName?: string;
  phone?: string;
  // Identity document
  idType?: string;
  idNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  // Bank account
  bankName?: string;
  accountType?: string;
  accountNumber?: string;
  routingNumber?: string;
  // Secure note
  body?: string;
  // Wi-Fi
  ssid?: string;
  // Medical portal
  patientId?: string;
  portalUrl?: string;
  // TOTP / 2FA — base32 secret (stored encrypted, never leaves device in plaintext)
  otp_secret?: string;
}

export interface CredentialChangeLogEntry {
  at: string;
  by: string;
  name: string;
  fields: string[];
}

export interface EncryptedCredential {
  id: number;
  credential_type: string;
  label: string;
  url: string | null;
  username_hint: string | null;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  vault_version: number;
  watch_shared: boolean;
  change_log: CredentialChangeLogEntry[];
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Derives the per-project Vault Encryption Key from account-level inputs + a
 * server-stored random salt. 100k PBKDF2 iterations is intentionally slow to
 * resist offline brute-force if the encrypted blobs are ever leaked.
 */
export async function deriveVaultKey(
  accountId: string,
  projectId: number | string,
  saltHex: string,
): Promise<Uint8Array> {
  const input = utf8ToBytes(`${accountId}:${projectId}:vault-v1`);
  const salt = hexToBytes(saltHex);
  return pbkdf2Async(sha256, input, salt, { c: 100_000, dkLen: 32 });
}

async function writeVekSecure(key: string, hexVek: string): Promise<void> {
  const opts = {
    requireAuthentication: true,
    authenticationPrompt: 'Confirm identity to unlock vault',
    keychainAccessible: KEYCHAIN_ACCESSIBLE,
  };
  try {
    await SecureStore.setItemAsync(key, hexVek, opts);
  } catch {
    // Android Keystore does not allow in-place updates of biometric-protected keys
    await SecureStore.deleteItemAsync(key).catch(() => {});
    await SecureStore.setItemAsync(key, hexVek, opts);
  }
}

/** Cache the derived VEK in the device's secure enclave / Android Keystore. */
export async function cacheVaultKey(projectId: number | string, vek: Uint8Array): Promise<void> {
  if (VAULT_PERSISTENCE_DISABLED) return;
  await writeVekSecure(vekKey(projectId), bytesToHex(vek));
}

/**
 * Load the cached VEK from SecureStore. Triggers the biometric prompt.
 * Returns null if not found or if the user cancels authentication.
 *
 * On plain web, always returns null — VEK persistence is disabled there
 * (see VAULT_PERSISTENCE_DISABLED). We also defensively scrub any legacy key
 * left behind from before this guard landed, in case a user upgraded from
 * an older build that had cached the VEK to localStorage.
 */
export async function loadVaultKey(projectId: number | string): Promise<Uint8Array | null> {
  if (VAULT_PERSISTENCE_DISABLED) {
    await SecureStore.deleteItemAsync(vekKey(projectId)).catch(() => {});
    return null;
  }
  try {
    const hex = await SecureStore.getItemAsync(vekKey(projectId), {
      requireAuthentication: true,
      authenticationPrompt: 'Unlock vault',
    });
    return hex ? hexToBytes(hex) : null;
  } catch {
    return null;
  }
}

/** Remove the cached VEK (e.g. on sign-out or vault reset). */
export async function clearVaultKey(projectId: number | string): Promise<void> {
  await SecureStore.deleteItemAsync(vekKey(projectId)).catch(() => {});
}

// ─── Share URL persistence ───────────────────────────────────────────────────
//
// A share URL like https://eleplan.link/abc#<otkHex> contains the share's
// one-time decryption key in the fragment. The server never sees the fragment;
// it just resolves the slug to an encrypted blob. So a stored share URL is
// effectively a stored credential. Same persistence rules as the VEK apply.
const shareUrlKey = (code: string) => `vault_share_url_${code}`;

/**
 * Persist a share URL keyed by its server-issued share code, so the shares
 * manager can re-display the URL (with the OTK fragment) for the user to
 * copy / re-send. No-op on plain web — see VAULT_PERSISTENCE_DISABLED.
 */
export async function cacheShareUrl(code: string, url: string): Promise<void> {
  if (VAULT_PERSISTENCE_DISABLED) return;
  await SecureStore.setItemAsync(shareUrlKey(code), url).catch(() => {});
}

/**
 * Load a previously persisted share URL by code. Returns null when missing
 * or when persistence is disabled (plain web); also defensively scrubs any
 * legacy URL left behind from before this guard landed.
 */
export async function loadShareUrl(code: string): Promise<string | null> {
  if (VAULT_PERSISTENCE_DISABLED) {
    await SecureStore.deleteItemAsync(shareUrlKey(code)).catch(() => {});
    return null;
  }
  return SecureStore.getItemAsync(shareUrlKey(code)).catch(() => null);
}

/** Remove the persisted share URL (e.g. on revoke or share expiry). */
export async function clearShareUrl(code: string): Promise<void> {
  await SecureStore.deleteItemAsync(shareUrlKey(code)).catch(() => {});
}

/**
 * Generate a random 32-byte one-time key for a single credential share.
 * The caller embeds this key in the URL fragment — it never leaves the client
 * and is never sent to the server.
 */
export async function generateShareKey(): Promise<Uint8Array> {
  const buf = await Crypto.getRandomBytesAsync(32);
  return new Uint8Array(buf);
}

/** Encrypt a credential payload with AES-256-GCM. */
export async function encryptCredential(
  vek: Uint8Array,
  payload: DecryptedCredential,
): Promise<{ encrypted_data: string; iv: string; auth_tag: string }> {
  const ivBuffer = await Crypto.getRandomBytesAsync(12);
  const iv = new Uint8Array(ivBuffer);
  const plaintext = utf8ToBytes(JSON.stringify(payload));
  const cipher = gcm(vek, iv);
  const ciphertext = cipher.encrypt(plaintext);
  const data = ciphertext.slice(0, -16);
  const tag = ciphertext.slice(-16);
  return {
    encrypted_data: bytesToHex(data),
    iv: bytesToHex(iv),
    auth_tag: bytesToHex(tag),
  };
}

/** Decrypt a stored credential. Returns null if the key or ciphertext is invalid. */
export function decryptCredential(
  vek: Uint8Array,
  encrypted_data: string,
  iv: string,
  auth_tag: string,
): DecryptedCredential | null {
  try {
    const ivBytes = hexToBytes(iv);
    const dataBytes = hexToBytes(encrypted_data);
    const tagBytes = hexToBytes(auth_tag);
    const ciphertext = new Uint8Array(dataBytes.length + tagBytes.length);
    ciphertext.set(dataBytes);
    ciphertext.set(tagBytes, dataBytes.length);
    const cipher = gcm(vek, ivBytes);
    const plaintext = cipher.decrypt(ciphertext);
    return JSON.parse(new TextDecoder().decode(plaintext)) as DecryptedCredential;
  } catch {
    return null;
  }
}
